import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Lightbulb, TrendingUp, Settings, Compass, ArrowRight, X } from 'lucide-react';

const stages = [
  {
    id: 'startup',
    title: 'Startup',
    subtitle: 'The Idea',
    icon: Lightbulb,
    color: '#4FC3F7',
    challenges: [
      'Is my idea actually viable in the market?',
      'How do I price my products or services?',
      'What will it cost to get started?',
      'How do I create realistic financial projections?',
      'How can I present my idea professionally to investors?'
    ],
    services: [
      'Feasibility studies to test if your idea will work',
      'Complete business plans that clearly explain your vision',
      'Startup financial models with realistic revenue and cost forecasts',
      'Pricing strategy recommendations',
      'Help identifying potential funding sources'
    ],
    storyTemplate: 'If {businessName} is just an idea with potential, EYB can help turn that vision into a structured business plan with clear financial goals and a roadmap for success.'
  },
  {
    id: 'growth',
    title: 'Growth',
    subtitle: 'Funding & Expansion',
    icon: TrendingUp,
    color: '#29ABE2',
    challenges: [
      'How do I secure funding for expansion?',
      'What funding option is right for me - loans, grants, or investors?',
      'How do I create documents that investors will trust?',
      'How do I manage cash flow during rapid growth?',
      'What do banks and investors need to see?',
      'How do I present my growth story convincingly?'
    ],
    services: [
      'Funding-ready financial models that show your growth potential',
      'Professional investor decks and pitch materials',
      '3-way financial models (Profit & Loss, Balance Sheet, Cash Flow)',
      'Help choosing the right funding sources for your situation',
      'Grant application preparation and support',
      'Financial scenarios showing how investment drives growth'
    ],
    storyTemplate: 'As {businessName} starts to grow, EYB can prepare professional forecasts and funding-ready plans to help secure the capital needed for expansion and sustainable growth.',
    ctaText: 'Get Help Getting Funding Ready'
  },
  {
    id: 'scaling',
    title: 'Scaling',
    subtitle: 'Managing Growth',
    icon: Settings,
    color: '#1E88C7',
    challenges: [
      'How do I manage cash flow as we grow quickly?',
      'What financial systems and controls do I need?',
      'Which metrics should I be tracking?',
      'How do I ensure we have enough working capital?',
      'How do I maintain profitability while scaling?',
      'How do I get real-time visibility into my finances?'
    ],
    services: [
      'Working capital management programs',
      'Real-time cash flow dashboards',
      'Custom KPI tracking systems for your business',
      'Financial processes and controls that grow with you',
      'Budgeting and forecasting systems',
      'Ongoing financial analysis and insights'
    ],
    storyTemplate: 'When {businessName} has momentum but needs better control, EYB can introduce dashboards and systems to manage finances and track performance in real time.',
    ctaText: 'Know More About Business Management'
  },
  {
    id: 'established',
    title: 'Established',
    subtitle: 'Strategic Leadership',
    icon: Compass,
    color: '#0B2545',
    challengesTitle: 'Questions business owners often ask',
    challenges: [
      'How do I stay ahead as competition increases?',
      'Where should I invest next, and what can wait?',
      'Should I expand into new products, services, or markets?',
      'How do I spot risks early and stay in control?',
      'I need experienced guidance, but not a full-time hire. What is the right option?'
    ],
    servicesTitle: 'How EYB Business Mentoring Helps',
    services: [
      'Ongoing business mentor support, without the cost of a full-time role',
      'Clear, independent thinking for important business decisions',
      'Practical risk management so there are fewer surprises',
      'Simple, board-style reporting and scenario discussions so you can see what is coming and plan with confidence'
    ],
    storyTemplate: 'As {businessName} becomes established, EYB can act as a trusted partner, helping shape long-term strategy and financial direction for sustained success.',
    ctaText: 'Talk To A Business Mentor'
  }
];

const JourneyMap = () => {
  const [businessName, setBusinessName] = useState('');
  const [selectedStage, setSelectedStage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const name = localStorage.getItem('eybBusinessName');
    if (!name) {
      navigate('/');
    } else {
      setBusinessName(name);
    }
  }, [navigate]);

  const handleStageClick = (stage) => {
    setSelectedStage(stage);
  };

  const getStoryText = (template) => {
    return template.replace('{businessName}', businessName);
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-mesh">
      {/* Layered animated background */}
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none"></div>
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl animate-pulse-slow" style={{ background: 'radial-gradient(circle, rgba(91,160,208,0.35), transparent 70%)' }}></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl animate-pulse-slow" style={{ background: 'radial-gradient(circle, rgba(0,51,102,0.25), transparent 70%)', animationDelay: '1.5s' }}></div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_e62fe2c4-7b06-4359-9dcf-9bd67772605b/artifacts/k7n3nl06_Screenshot%202024-07-25%20103325.png" 
            alt="EYB Consulting" 
            className="h-12 w-auto cursor-pointer smooth-transition hover:opacity-80"
            onClick={() => navigate('/')}
          />
          <div className="flex items-center gap-4">
            <span className="text-lg font-semibold" style={{ color: '#0B0F14' }}>
              {businessName}'s Journey
            </span>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-16">
        {/* Title section */}
        <div className="text-center mb-20 animate-fadeInUp">
          <h1 className="text-5xl md:text-6xl font-semibold mb-6 tracking-tight" style={{ color: '#0B2545' }}>
            <span className="gradient-text-animated">Your Business Journey</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Explore each stage to discover how EYB supports businesses like <span className="font-semibold" style={{ color: '#0B2545' }}>{businessName}</span> at every step.
          </p>
        </div>

        {/* Journey stages */}
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {stages.map((stage, index) => {
            const Icon = stage.icon;
            return (
              <div
                key={stage.id}
                className="animate-fadeInUp"
                style={{ animationDelay: `${index * 0.12}s`, opacity: 0, animationFillMode: 'forwards' }}
              >
                <div
                  onClick={() => handleStageClick(stage)}
                  data-testid={`stage-card-${stage.id}`}
                  className="group relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 cursor-pointer hover-lift smooth-transition h-full overflow-hidden"
                  style={{
                    boxShadow: '0 4px 20px rgba(0, 51, 102, 0.08)'
                  }}
                >
                  {/* Animated gradient border on hover */}
                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 smooth-transition pointer-events-none"
                    style={{
                      padding: '2px',
                      background: `linear-gradient(135deg, ${stage.color}, #B3E5FC, ${stage.color})`,
                      backgroundSize: '200% 200%',
                      animation: 'gradientShift 4s ease infinite',
                      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude',
                    }}
                  ></div>

                  {/* Glow blob */}
                  <div
                    className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-0 group-hover:opacity-60 blur-2xl smooth-transition pointer-events-none"
                    style={{ background: stage.color }}
                  ></div>

                  {/* Icon */}
                  <div className="mb-6 relative">
                    <div
                      className="w-20 h-20 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 smooth-transition shadow-md"
                      style={{ background: `linear-gradient(135deg, ${stage.color}20, ${stage.color}45)` }}
                    >
                      <Icon className="w-10 h-10 group-hover:scale-110 smooth-transition" style={{ color: stage.color }} strokeWidth={2.2} />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold mb-2 relative" style={{ color: stage.color }}>
                    {stage.title}
                  </h3>
                  <p className="text-gray-600 font-medium mb-4 relative">
                    {stage.subtitle}
                  </p>
                  <p className="text-sm font-semibold flex items-center gap-1 relative" style={{ color: stage.color }}>
                    <span className="opacity-70 group-hover:opacity-100 smooth-transition">Click to explore</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 smooth-transition" />
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fadeInUp" style={{ animationDelay: '0.6s', opacity: 0, animationFillMode: 'forwards' }}>
          <Button
            onClick={() => navigate('/services')}
            className="px-8 py-6 text-base font-bold group smooth-transition tracking-wide"
            style={{ background: '#0B2545', color: '#ffffff', borderRadius: '9999px', boxShadow: '0 8px 24px -8px rgba(11, 37, 69, 0.4)' }}
          >
            Explore Our Services
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 smooth-transition" />
          </Button>
          <Button
            onClick={() => navigate('/contact')}
            variant="outline"
            className="px-8 py-6 text-base font-bold border-2 smooth-transition hover:bg-[#0B2545] hover:text-white tracking-wide"
            style={{ borderColor: '#0B2545', color: '#0B2545', background: 'transparent', borderRadius: '9999px' }}
          >
            Get Started
          </Button>
        </div>
      </main>

      {/* Stage detail modal */}
      <Dialog open={!!selectedStage} onOpenChange={() => setSelectedStage(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto animate-modalBounce">
          {selectedStage && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4 mb-4 animate-slideInLeft">
                  <div 
                    className="w-16 h-16 rounded-xl flex items-center justify-center animate-float"
                    style={{ background: `linear-gradient(135deg, ${selectedStage.color}20, ${selectedStage.color}40)` }}
                  >
                    {React.createElement(selectedStage.icon, { 
                      className: "w-8 h-8", 
                      style: { color: selectedStage.color } 
                    })}
                  </div>
                  <div>
                    <DialogTitle className="text-3xl" style={{ color: selectedStage.color }}>
                      {selectedStage.title}
                    </DialogTitle>
                    <DialogDescription className="text-lg">
                      {selectedStage.subtitle}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-6">
                {/* Story */}
                <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-6 border border-sky-200 animate-fadeInUp" style={{ animationDelay: '0.1s', opacity: 0, animationFillMode: 'forwards' }}>
                  <p className="text-lg leading-relaxed" style={{ color: '#0B0F14' }}>
                    {getStoryText(selectedStage.storyTemplate)}
                  </p>
                </div>

                {/* Challenges */}
                <div className="animate-fadeInUp" style={{ animationDelay: '0.2s', opacity: 0, animationFillMode: 'forwards' }}>
                  <h4 className="text-xl font-bold mb-3" style={{ color: '#0B0F14' }}>
                    {selectedStage.challengesTitle || 'Common Challenges Faced by Businesses'}
                  </h4>
                  <ul className="space-y-2">
                    {selectedStage.challenges.map((challenge, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-gray-600 animate-slideInLeft" style={{ animationDelay: `${0.3 + idx * 0.05}s`, opacity: 0, animationFillMode: 'forwards' }}>
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: selectedStage.color }}></span>
                        <span>{challenge}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Services */}
                <div className="animate-fadeInUp" style={{ animationDelay: '0.4s', opacity: 0, animationFillMode: 'forwards' }}>
                  <h4 className="text-xl font-bold mb-3" style={{ color: '#0B0F14' }}>
                    {selectedStage.servicesTitle || 'How EYB Helps'}
                  </h4>
                  <ul className="space-y-2">
                    {selectedStage.services.map((service, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-gray-600 animate-slideInRight" style={{ animationDelay: `${0.5 + idx * 0.05}s`, opacity: 0, animationFillMode: 'forwards' }}>
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: selectedStage.color }}></span>
                        <span>{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <Button
                  onClick={() => navigate('/contact')}
                  className="w-full py-6 text-base font-bold group animate-fadeInUp tracking-wide"
                  style={{
                    background: '#0B2545',
                    color: '#ffffff',
                    borderRadius: '9999px',
                    boxShadow: '0 8px 24px -8px rgba(11, 37, 69, 0.4)',
                    animationDelay: '0.7s',
                    opacity: 0,
                    animationFillMode: 'forwards'
                  }}
                >
                  {selectedStage.ctaText || `Get Help with ${selectedStage.title}`}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 smooth-transition" />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JourneyMap;